/**
 * R13 — Visitor history, grouped Today / Yesterday / This week / Earlier.
 * Wired: GET /api/visitors. Port of screens-visitor.jsx VisitorsScreen.
 */
import { router } from 'expo-router';
import React from 'react';
import { Image, Pressable, View } from 'react-native';

import { space, type PillStatus } from '@bero/tokens';
import { fmtWhen, groupFor, str, useGateList, GROUP_ORDER, type Group } from '@/components/gate/api';
import { Icon } from '@/components/gate/icons';
import { useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { useTheme } from '@/theme';
import { AppText, Button, Card, EmptyState, IconButton, Pill, Screen, Skeleton, TopBar } from '@/ui';

/** Arch-shaped avatar — the design's signature visitor-history motif.
 * Shows the gate photo when one exists, initials otherwise. */
function ArchAvatar({ name, photo, size = 48, tone = 'primary' }: {
  name: string; photo?: string; size?: number; tone?: 'primary' | 'neutral';
}) {
  const { colors } = useTheme();
  const initials = name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const bg = tone === 'primary' ? colors.primary100 : colors.canvas2;
  const fg = tone === 'primary' ? colors.primary900 : colors.textSecondary;
  const shape = {
    width: size, height: size * 1.15,
    borderTopLeftRadius: size / 2, borderTopRightRadius: size / 2,
    borderBottomLeftRadius: size * 0.11, borderBottomRightRadius: size * 0.11,
    overflow: 'hidden' as const,
  };
  if (photo) {
    return <Image source={{ uri: photo }} style={[shape, { backgroundColor: colors.canvas2 }]} resizeMode="cover" />;
  }
  return (
    <View style={[shape, { backgroundColor: bg, alignItems: 'center', justifyContent: 'center' }]}>
      <AppText weight="700" style={{ color: fg, fontSize: size * 0.28, lineHeight: size * 0.36 }}>{initials}</AppText>
    </View>
  );
}

type VisitorRow = {
  id?: string; name: string; purpose: string; when: string; status: PillStatus; group: Group;
  photo?: string;
};

const STATUSES: PillStatus[] = ['open', 'progress', 'urgent', 'resolved', 'approved', 'pending', 'rejected'];

/** Localised label for each visit-history group (values from GROUP_ORDER). */
const GROUP_LABEL: Record<Group, DictKey> = {
  Today: 'time_today',
  Yesterday: 'time_yesterday',
  'This week': 'bd_this_week',
  Earlier: 'vis_group_earlier',
};

// entry-type enum → localised label (free-text purposes/company names pass through).
const TYPE_KEY: Record<string, DictKey> = {
  VISITOR: 'vd_type_guest', DELIVERY: 'vd_type_delivery', CAB: 'vd_type_cab', STAFF: 'vd_type_staff',
};

function toRow(v: unknown): VisitorRow {
  const rawStatus = (str(v, 'status') ?? 'pending').toLowerCase();
  const status: PillStatus = (STATUSES as string[]).includes(rawStatus) ? (rawStatus as PillStatus) : 'pending';
  const iso = str(v, 'arrivedAt', 'createdAt', 'visitedAt', 'time');
  return {
    id: str(v, 'id'),
    photo: str(v, 'photo'),
    name: str(v, 'name', 'visitorName') ?? 'Visitor',
    purpose: str(v, 'purpose', 'reason', 'type') ?? 'Visitor',
    when: fmtWhen(iso, str(v, 'time', 'when')),
    status,
    group: groupFor(iso, str(v, 'group')),
  };
}

export default function VisitorsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { items, state } = useGateList<unknown>('/api/visitors', 'visitors');
  const rows = items.map(toRow);
  const groups = GROUP_ORDER.filter((g) => rows.some((r) => r.group === g));

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('vis_title')}
        onBack={() => router.back()}
        right={
          <IconButton label={t('vis_filter')} onPress={() => { /* TODO(wire): filter sheet */ }}>
            <Icon name="filter" size={20} color={colors.textPrimary} />
          </IconButton>
        }
      />
      <Screen padded style={{ paddingTop: space.s2 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3, marginTop: space.s4 }}>
            <Skeleton w={96} h={14} />
            {[0, 1, 2].map((i) => <Skeleton key={i} h={84} />)}
          </View>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="users" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? t('vis_err_title') : t('vis_empty_title')}
            body={state === 'error'
              ? t('vis_err_body')
              : t('vis_empty_body')}
            cta={t('vis_preapprove')}
            onCta={() => router.push('/preapprove')}
          />
        ) : (
          <>
            {groups.map((g) => (
              <View key={g}>
                <AppText variant="bodyMd" weight="500" style={{ marginTop: space.s4, marginBottom: 10 }}>{t(GROUP_LABEL[g])}</AppText>
                <View style={{ gap: space.s3 }}>
                  {rows.filter((r) => r.group === g).map((r, i) => (
                    <Pressable
                      key={`${g}-${i}`}
                      disabled={!r.id}
                      onPress={() => router.push(`/visitors/${r.id}`)}
                      accessibilityRole="button"
                      accessibilityLabel={`Visit details for ${r.name}`}
                    >
                      <Card>
                        <View style={{ flexDirection: 'row', gap: space.s3, alignItems: 'center' }}>
                          <ArchAvatar name={r.name} photo={r.photo} size={48} tone={i % 2 === 0 ? 'primary' : 'neutral'} />
                          <View style={{ flex: 1, minWidth: 0 }}>
                            <AppText variant="headingSm" weight="500">{r.name}</AppText>
                            <AppText variant="bodySm" tone="secondary" style={{ marginTop: 2 }}>
                              {TYPE_KEY[r.purpose.toUpperCase()] ? t(TYPE_KEY[r.purpose.toUpperCase()]) : r.purpose}
                            </AppText>
                            {r.when ? <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>{r.when}</AppText> : null}
                          </View>
                          <Pill status={r.status} />
                          {r.id ? <Icon name="chevron-right" size={18} color={colors.textTertiary} /> : null}
                        </View>
                      </Card>
                    </Pressable>
                  ))}
                </View>
              </View>
            ))}
            <Button
              title={t('vis_load_earlier')}
              kind="tertiary"
              style={{ marginTop: space.s4, alignSelf: 'flex-start' }}
              onPress={() => { /* TODO(wire): pagination ?page=2 */ }}
            />
          </>
        )}
      </Screen>
    </Screen>
  );
}
